import { downloadData, getUrl } from "aws-amplify/storage";

/**
 * Detect if running in app browser (like email clients, social media apps, etc.)
 * @returns {boolean} True if running in an app browser environment
 */
export const isAppBrowser = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  
  // Check for common app browser patterns
  const appBrowserPatterns = [
    /WebView/i,
    /wv\)/i,
    /Android.*Version\/.*Chrome/i,
    /iPhone.*Version\/.*Safari/i,
    /iPad.*Version\/.*Safari/i,
    /FBAN/i, // Facebook
    /FBAV/i, // Facebook
    /Instagram/i,
    /Twitter/i,
    /LinkedInApp/i,
    /WhatsApp/i,
    /Telegram/i,
    /Line/i,
    /MicroMessenger/i, // WeChat
    /QQ/i,
    /Snapchat/i,
    /TikTok/i,
    /Pinterest/i,
    /Reddit/i,
    /Slack/i,
    /Discord/i,
    /Zoom/i,
    /Teams/i,
    /Outlook/i,
    /Gmail/i,
    /YahooMail/i,
  ];

  return appBrowserPatterns.some(pattern => pattern.test(userAgent));
};

/**
 * Get MIME type from file extension
 * @param {string} filename - The filename with extension
 * @returns {string} MIME type
 */
export const getMimeType = (filename) => {
  if (!filename) return 'application/octet-stream';
  
  const extension = filename.split('.').pop()?.toLowerCase();
  
  const mimeTypes = {
    // Documents
    'pdf': 'application/pdf',
    'doc': 'application/msword',
    'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'xls': 'application/vnd.ms-excel',
    'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'ppt': 'application/vnd.ms-powerpoint',
    'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'txt': 'text/plain',
    'rtf': 'application/rtf',
    
    // Images
    'jpg': 'image/jpeg',
    'jpeg': 'image/jpeg',
    'png': 'image/png',
    'gif': 'image/gif',
    'bmp': 'image/bmp',
    'webp': 'image/webp',
    'svg': 'image/svg+xml',
    'ico': 'image/x-icon',
    
    // Audio
    'mp3': 'audio/mpeg',
    'wav': 'audio/wav',
    'ogg': 'audio/ogg',
    'aac': 'audio/aac',
    'm4a': 'audio/mp4',
    
    // Video
    'mp4': 'video/mp4',
    'avi': 'video/x-msvideo',
    'mov': 'video/quicktime',
    'wmv': 'video/x-ms-wmv',
    'flv': 'video/x-flv',
    'webm': 'video/webm',
    
    // Archives
    'zip': 'application/zip',
    'rar': 'application/vnd.rar',
    '7z': 'application/x-7z-compressed',
    'tar': 'application/x-tar',
    'gz': 'application/gzip',
    
    // Other
    'json': 'application/json',
    'xml': 'application/xml',
    'csv': 'text/csv',
    'html': 'text/html',
    'css': 'text/css',
    'js': 'application/javascript',
  };
  
  return mimeTypes[extension] || 'application/octet-stream';
};

/**
 * Enhanced download blob function with app browser fallbacks and MIME type support
 * @param {Blob} blob - The blob to download
 * @param {string} filename - The filename for the download
 * @param {string} signedUrl - Optional signed URL for app browsers
 * @returns {HTMLAnchorElement|null} The created anchor element or null
 */
export const downloadBlob = (blob, filename, signedUrl = null) => {
  const finalFilename = filename || "download";
  
  if (isAppBrowser()) {
    // For app browsers, use signed URL if available, otherwise skip
    if (signedUrl) {
      window.open(signedUrl, '_blank');
    }
    return null;
  } else {
    // Standard download for regular browsers
    const mimeType = getMimeType(finalFilename);
    
    // Create a new blob with proper MIME type if needed
    const typedBlob = blob.type ? blob : new Blob([blob], { type: mimeType });
    const url = URL.createObjectURL(typedBlob);
    
    // Create download element
    const a = document.createElement("a");
    a.style.display = "none";
    a.href = url;
    a.download = finalFilename;
    
    const clickHandler = () => {
      setTimeout(() => {
        URL.revokeObjectURL(url);
        a.removeEventListener("click", clickHandler);
        if (document.body.contains(a)) {
          document.body.removeChild(a);
        }
      }, 150);
    };
    
    a.addEventListener("click", clickHandler, false);
    document.body.appendChild(a);
    a.click();
    return a;
  }
};

/**
 * Enhanced error handler for download failures with app browser support
 * @param {string} key - The file key to download
 * @param {Function} setError - Error setter function
 */
export const handleDownloadError = async (key, setError) => {
  try {
    const urlResult = await getUrl({
      path: ({ identityId }) => `private/${identityId}/${key}`,
      options: {
        useAccelerateEndpoint: true,
      },
    });
    
    const signedURL = urlResult.url.href;
    
    if (isAppBrowser()) {
      // For app browsers, try window.open first
      const newWindow = window.open(signedURL, '_blank');
      if (!newWindow) {
        // If popup blocked, show confirmation and use location redirect
        const userConfirmed = window.confirm(
          "Popup blocked. Click OK to open the download in this tab."
        );
        if (userConfirmed) {
          window.location.href = signedURL;
        }
      }
    } else {
      // Standard behavior for regular browsers
      window.open(signedURL, '_blank');
    }
  } catch (signedURLError) {
    if (setError) {
      setError(signedURLError);
    }
  }
};

/**
 * Extract filename with proper extension from a file key or path
 * @param {string} key - The file key or path
 * @returns {string} Properly formatted filename with extension
 */
export const extractFilename = (key) => {
  if (!key) return "download";
  
  // Remove any path prefixes and get the actual filename
  const filename = key.split('/').pop() || key;
  
  // If filename already has an extension, return as is
  if (filename.includes('.') && filename.lastIndexOf('.') > 0) {
    return filename;
  }
  
  // If no extension, try to infer from the key structure
  // Common patterns: folder/filename.ext or just filename
  const keyParts = key.split('.');
  if (keyParts.length > 1) {
    const extension = keyParts[keyParts.length - 1];
    // Only add extension if it looks like a valid file extension (2-5 chars, alphanumeric)
    if (/^[a-zA-Z0-9]{2,5}$/.test(extension)) {
      return filename.includes('.') ? filename : `${filename}.${extension}`;
    }
  }
  
  return filename || "download";
};

/**
 * Downloads a file with fallback mechanism
 * @param {string} key - File key to download
 * @param {Function} onProgress - Progress callback function
 * @param {Function} getFileName - Function to get filename from key
 * @param {Object} downloadRef - Ref object to store download request
 */
export const downloadFileWithFallback = async (
  key,
  onProgress,
  getFileName,
  downloadRef
) => {
  try {
    const request = downloadData({
      path: ({ identityId }) => `private/${identityId}/${key}`,
      options: {
        useAccelerateEndpoint: true,
        onProgress: onProgress,
      },
    });

    if (downloadRef) {
      downloadRef.current = request;
    }
    
    const result = await request.result;
    
    // Enhanced filename handling
    let fileName;
    if (getFileName && typeof getFileName === 'function') {
      fileName = getFileName(key);
    } else {
      fileName = extractFilename(key);
    }
    
    // Ensure we have a valid filename
    fileName = fileName || extractFilename(key) || "download";
    
    // For app browsers, get signed URL and pass it to downloadBlob
    if (isAppBrowser()) {
      try {
        const urlResult = await getUrl({
          path: ({ identityId }) => `private/${identityId}/${key}`,
          options: {
            useAccelerateEndpoint: true,
          },
        });
        downloadBlob(result.body, fileName, urlResult.url.href);
      } catch (urlError) {
        // If signed URL fails, just use blob without URL
        downloadBlob(result.body, fileName);
      }
    } else {
      downloadBlob(result.body, fileName);
    }
  } catch (downloadError) {
    if (downloadError.message !== "canceled") {
      // Try alternative download method as fallback
      try {
        const urlResult = await getUrl({
          path: ({ identityId }) => `private/${identityId}/${key}`,
          options: {
            useAccelerateEndpoint: true,
          },
        });
        
        if (isAppBrowser()) {
          // For app browsers, directly open the signed URL
          window.open(urlResult.url.href, '_blank');
        } else {
          // For regular browsers, try to preserve filename
          const fileName = getFileName ? getFileName(key) : extractFilename(key);
          const link = document.createElement('a');
          link.href = urlResult.url.href;
          link.download = fileName;
          link.style.display = 'none';
          document.body.appendChild(link);
          
          try {
            link.click();
            document.body.removeChild(link);
          } catch (clickError) {
            // If click fails, fallback to direct navigation
            document.body.removeChild(link);
            window.location.href = urlResult.url.href;
          }
        }
      } catch (fallbackError) {
        // Re-throw error to be handled by the calling component
        throw fallbackError;
      }
    }
  }
};

/**
 * Create alternative download button handler for app browsers
 * @param {string} key - The file key to download
 * @param {Function} setError - Error setter function
 * @returns {Function} Click handler function
 */
export const createAlternativeDownloadHandler = (key, setError) => {
  return async () => {
    try {
      const urlResult = await getUrl({
        path: ({ identityId }) => `private/${identityId}/${key}`,
        options: {
          useAccelerateEndpoint: true,
        },
      });
      window.location.href = urlResult.url.href;
    } catch (error) {
      if (setError) {
        setError(error);
      }
    }
  };
};